import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import { Contract, MediaAsset, MediaAssetType, PetPurchase } from 'shared/@types';
import { KeyValueErrors } from 'shared/library/use-api';

export interface ContractWizardSupport {
  petPurchase: PetPurchase | null;
  useContract: boolean | null;
  petRegistrationId: string | null;
  viewedPrice: boolean;
  viewedAttachments: boolean;
  attachments: MediaAsset[];
}

const defaultSupport: ContractWizardSupport = {
  petPurchase: null,
  useContract: null,
  petRegistrationId: null,
  viewedPrice: false,
  viewedAttachments: false,
  attachments: [],
};

const useContractWizardStore = create(
  combine(
    {
      data: null as Contract | null,
      support: defaultSupport,
      errors: {} as KeyValueErrors,
    },
    (set) => ({
      setup: (petPurchase: PetPurchase | null | undefined) => {
        const contract = petPurchase?.contract;
        const pet = petPurchase?.pet;
        const mediaAssets = Array.isArray(contract?.media_assets) ? contract.media_assets : [];

        if (!petPurchase || !contract || !pet) {
          set({
            data: null,
            support: defaultSupport,
          });

          return;
        }

        set({
          data: contract,
          support: {
            petPurchase,
            useContract: contract.deleted_at ? false : contract.mom_name ? true : null,
            petRegistrationId: pet.registration_id,
            viewedPrice: contract.pet_delivery_date !== null,
            viewedAttachments: mediaAssets.length > 0,
            attachments: mediaAssets.filter((a) => a.type !== MediaAssetType.Signature),
          },
        });
      },
      reset: () =>
        set({
          data: null,
          support: defaultSupport,
          errors: {},
        }),
      setData: (newData: Partial<Contract>) => {
        set((state) => ({ data: { ...state.data!, ...newData } }));
      },
      setSupport: (newData: Partial<ContractWizardSupport>) => {
        set((state) => ({ support: { ...state.support, ...newData } }));
      },
      addAttachment: (attachment: MediaAsset) => {
        set((state) => ({
          support: { ...state.support, attachments: [...state.support.attachments, attachment] },
        }));
      },
      removeAttachment: (attachment: MediaAsset) => {
        set((state) => ({
          support: {
            ...state.support,
            attachments: state.support.attachments.filter((a) => a.uuid !== attachment.uuid),
          },
        }));
      },
    }),
  ),
);

export default useContractWizardStore;
